import { LitElement, html, css } from 'lit';

export class CrusherDisclosure extends LitElement {
  static styles = css`
    :host {
      display: block;
      interpolate-size: allow-keywords;
    }
    :host([hidden]) {
      display: none;
    }

    details {
      border: var(--crusher-component-border-weight) solid var(--crusher-border-primary);
      border-radius: var(--crusher-radius-md);
      overflow: hidden;
    }

    summary {
      cursor: pointer;
      font-weight: var(--crusher-font-weight-semibold);
      list-style: none;
      padding: var(--crusher-spacing-3) var(--crusher-spacing-4);
      user-select: none;
      display: flex;
      align-items: center;
      gap: var(--crusher-spacing-2);
    }

    summary::-webkit-details-marker {
      display: none;
    }

    .summary__chevron {
      display: inline-block;
      flex-shrink: 0;
      transition: transform var(--crusher-motion-duration-fast, 120ms) var(--crusher-motion-easing-standard, ease);
    }

    details[open] .summary__chevron {
      transform: rotate(90deg);
    }

    summary:focus-visible {
      outline: var(--crusher-focus-width, 2px) solid var(--crusher-color-brand-primary);
      outline-offset: 2px;
    }

    .body {
      block-size: 0;
      overflow: hidden;
      transition: block-size var(--crusher-motion-duration-base, 200ms) var(--crusher-motion-easing-standard, ease);
      padding: 0 var(--crusher-spacing-4);
    }

    details[open] .body {
      block-size: auto;
      padding: var(--crusher-spacing-3) var(--crusher-spacing-4);
    }

    @media (prefers-reduced-motion: reduce) {
      .summary__chevron {
        transition: none;
      }
      .body {
        transition: none;
      }
    }
  `;

  static properties = {
    summary: { type: String, reflect: true },
    open:    { type: Boolean, reflect: true },
  };

  constructor() {
    super();
    this.summary = '';
    this.open = false;
  }

  // ---- Lifecycle ----

  firstUpdated() {
    // Sync the initial `open` property to the native <details> element.
    const details = this.shadowRoot.querySelector('details');
    if (details) {
      details.open = this.open;
    }
  }

  updated(changed) {
    if (changed.has('open')) {
      const details = this.shadowRoot.querySelector('details');
      if (details && details.open !== this.open) {
        details.open = this.open;
      }
    }
  }

  // ---- Handlers ----

  _onToggle(e) {
    const newOpen = e.target.open;
    if (this.open !== newOpen) {
      this.open = newOpen;
      this.dispatchEvent(new CustomEvent('crusher:disclosurechange', {
        detail: { open: newOpen },
        bubbles: true,
        composed: true,
      }));
    }
  }

  // ---- Render ----

  render() {
    return html`
      <details ?open=${this.open} @toggle=${this._onToggle}>
        <summary>
          <span class="summary__chevron" aria-hidden="true">&#9658;</span>
          <slot name="summary">${this.summary}</slot>
        </summary>
        <div class="body">
          <slot></slot>
        </div>
      </details>
    `;
  }
}

customElements.define('crusher-disclosure', CrusherDisclosure);
